/**
 * useCustomer360Live — invoke + poll the c360_search durable Lambda.
 *
 * 1. POST /invoke → get execution_arn from APIGW response header.
 * 2. Poll GET /status?execution_arn=... every 2s until terminal.
 * 3. Expose { status, steps, result, error } for progressive rendering.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  invokeCustomer360,
  pollCustomer360Status,
  type C360StepEvent,
} from "../lib/api";
import type { Customer360Response } from "../components/customer360/Customer360.types";

export interface UseCustomer360LiveReturn {
  /** IDLE | INVOKING | NOT_FOUND | RUNNING | SUCCEEDED | FAILED */
  status: string;
  /** Completed step events (name + timestamp). */
  steps: C360StepEvent[];
  /** Parsed C360 payload — populated on SUCCEEDED. */
  result: Customer360Response | null;
  /** Error details — populated on FAILED. */
  error: Record<string, unknown> | null;
  /** Re-trigger the lookup with the same or a new query. */
  invoke: (query?: string) => void;
}

const POLL_INTERVAL_MS = 2000;

export function useCustomer360Live(
  contactId: string,
  initialQuery: string,
  mode: string = "full",
  channel?: string | null,
  autoInvoke: boolean = true,
): UseCustomer360LiveReturn {
  const [status, setStatus] = useState("IDLE");
  const [steps, setSteps] = useState<C360StepEvent[]>([]);
  const [result, setResult] = useState<Customer360Response | null>(null);
  const [error, setError] = useState<Record<string, unknown> | null>(null);
  const executionArnRef = useRef<string | null>(null);
  const queryRef = useRef(initialQuery);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (execArn: string) => {
      stopPolling();
      const poll = async () => {
        try {
          const resp = await pollCustomer360Status(contactId, execArn);
          if (resp.status !== "NOT_FOUND") {
            setStatus(resp.status);
            setSteps(resp.steps);
          }
          if (resp.status === "SUCCEEDED" && resp.result) {
            setResult(resp.result as unknown as Customer360Response);
            stopPolling();
          } else if (resp.status === "FAILED") {
            setError(resp.error);
            stopPolling();
          }
        } catch (err) {
          console.warn("[useCustomer360Live] poll error:", err);
        }
      };
      void poll();
      timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    },
    [contactId, stopPolling],
  );

  const invoke = useCallback(
    (query?: string) => {
      const q = query ?? queryRef.current;
      queryRef.current = q;
      setStatus("INVOKING");
      setSteps([]);
      setResult(null);
      setError(null);
      invokeCustomer360(contactId, q, mode, channel)
        .then((resp) => {
          executionArnRef.current = resp.execution_arn;
          setStatus("RUNNING");
          startPolling(resp.execution_arn);
        })
        .catch((err) => {
          console.error("[useCustomer360Live] invoke error:", err);
          setStatus("FAILED");
          setError({ message: String(err) });
        });
    },
    [contactId, mode, channel, startPolling],
  );

  // Auto-invoke on mount (skipped when parent forwards external state).
  useEffect(() => {
    if (autoInvoke && initialQuery) invoke(initialQuery);
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, steps, result, error, invoke };
}
